import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { StyleSheet, View, TextInput, TouchableOpacity, Text, FlatList, Pressable, Platform, ScrollView, Dimensions, useWindowDimensions } from 'react-native';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { AntDesign, FontAwesome5, FontAwesome6, MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { InteractiveSearchTypes } from '../Types';
import searchStyles from '../searchStyles';
import { Checkbox, XStack } from 'tamagui';
import parseByDelimiter from '@utils/parseByDelimiter';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import AnimatedLottieView from '@utils/lottieAnimation';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import LoadingIndicator from '@components/Global/Spinner';
import {
  useHasActiveSubscriptionWithContext,
  useIsFreePlanWithContext,
  useAIUsageStatsForFreePlan
} from '@utils/billingUtils';

const AnimatedIcon = ({ name, theme }) => {
  const glyphSize = name === 'ellipsis-h' ? 24 : 20;
  return (
    <Animatable.View
      useNativeDriver
      animation="fadeInDown"
      duration={250}
      style={[searchStyles.iconStyle, { overflow: 'hidden', justifyContent: 'center' }]}
    >
      <FontAwesome5
        name={name}
        size={glyphSize}
        color={!theme ? 'white' : 'grey'}
        // flex on the glyph stretches its text box and pushes it off-centre; pin the line box instead.
        style={[searchStyles.searchIconStyle, { flex: 0, lineHeight: glyphSize, textAlign: 'center' }]}
      />
    </Animatable.View>
  );
};


const InteractiveSearch = React.forwardRef(({
  aiCallback,
  aiSearch,
  placeHolder,
  searchParams,
  callback,
  onValueChange,
  onFocus,
  onBlur,
  isExpanded = false,
  multiline = false,
  value,
  inputBorderRadius = 20,
  inputMinHeight,
  onSubmitAI,
  collapsableComponent,
  collapsedContent = 'Collapse',
  hideInput,
}: InteractiveSearchTypes, ref) => {
  const [focusedState, setFocusedState] = useState(false);
  const [text, setText] = useState(value || "");
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterArray, setFilterArray] = useState([]);
  const searchRef = useRef(null);
  const theme = themeSelector();
  const [AILoading, setAILoading] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [collapsed, setCollapsed] = useState(true);

  useImperativeHandle(ref, () => ({
    forceCollapse: () => {
      setCollapsed(true);
    }
  }));

  // Sync only when the parent `value` changes (clear, external reset, etc.).
  // Do NOT depend on local `text` — that races the debounced parent update and
  // reverts each keystroke (value still stale → setText(oldValue) → blocks typing).
  useEffect(() => {
    if (value !== undefined) {
      setText(value);
    }
  }, [value]);

  // Billing utilities for AI usage stats
  const hasActiveSubscription = useHasActiveSubscriptionWithContext();
  const isFreePlan = useIsFreePlanWithContext();
  const { data: aiUsageStats, isLoading: aiUsageLoading } = useAIUsageStatsForFreePlan();

  const clearSearch = useCallback(() => {
    setText("");
    onValueChange?.("");
    callback?.("");
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  }, [callback, onValueChange]);

  /** Web: multiline textarea needs keydown + preventDefault so Enter submits instead of a newline */
  const handleWebAISubmitKeyDown = useCallback(
    (e: { key: string; shiftKey?: boolean; preventDefault?: () => void }) => {
      if (!isExpanded || !aiSearch || !onSubmitAI) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault?.();
        void onSubmitAI();
      }
    },
    [isExpanded, aiSearch, onSubmitAI],
  );

  const handleNativeAISubmitKeyPress = useCallback(
    (e: { nativeEvent: { key: string; shiftKey?: boolean } }) => {
      if (!isExpanded || !aiSearch || !onSubmitAI) return;
      const { key, shiftKey } = e.nativeEvent as { key: string; shiftKey?: boolean };
      if (key !== 'Enter' || shiftKey) return;
      void onSubmitAI();
    },
    [isExpanded, aiSearch, onSubmitAI],
  );

  // Always use internal text state for display - it's synced with value prop when needed
  const displayValue = text;
  const hasValue = displayValue && displayValue.length > 0;

  const handler = useCallback((text: string) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      callback?.(text);
    }, 300);
  }, [callback]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const RowItem = ({ item, index }) => {
    const [checked, setChecked] = useState(false);

    const handleSelect = useCallback((checked, item) => {
      if (checked) {
        setFilterArray(value => [...value, item.dataTitle])
      }
    }, [])

    return (
      <View style={{ flexDirection: 'row' }}>
        <Checkbox status={checked ? "checked" : "unchecked"} onPress={() => { setChecked(!checked); handleSelect(checked) }} />
        <Text style={{ justifyContent: 'center', verticalAlign: 'middle' }}>{parseByDelimiter(item.dataTitle)}</Text>
      </View>
    )
  }

  useEffect(() => {
    if (filterArray.length > 0) {
      let newArray = [];
      searchParams.forEach(item => {
        if (filterArray.includes(item.id)) {
          newArray.push(item.dataSchema)
        }
      }
      )
    }
    return () => {
      setFilterArray([]);
    }
  }, [
    filterArray
  ])


  const containerHeight = inputMinHeight ?? (Platform.OS === 'android' ? 48 : 40);
  const isCompact = !multiline;
  const showCollapsableInsteadOfInput = isExpanded && !!collapsableComponent;

  const { width: screenWidth } = useWindowDimensions();
  const baseFontSize = screenWidth < 400 ? 12 : 12;
  const resolvedFontSize = responsiveFontSize(baseFontSize);
  const singleLineLineHeight = Math.max(
    Math.round(resolvedFontSize * 1.35),
    Platform.OS === 'android' ? 22 : 20,
  );

  return (
    <View style={{
      flexDirection: showCollapsableInsteadOfInput ? 'column' : 'row',
      alignItems: (showCollapsableInsteadOfInput || (focusedState && aiCallback)) ? 'stretch' : 'center',
      // Web dense row is already centred by the parent; the nudge only helps native line-height quirks.
      top: (focusedState && aiCallback) ? 0 : (Platform.OS === 'ios' ? -3 : 0),
      minHeight: containerHeight,
      borderRadius: inputBorderRadius,
      overflow: 'visible',
      position: (focusedState && aiCallback) ? 'absolute' : 'relative',
      width: '100%',
      flex: (focusedState && aiCallback) ? undefined : 1,
      left: (focusedState && aiCallback) ? 0 : undefined,
      right: (focusedState && aiCallback) ? 0 : undefined,
      zIndex: (collapsableComponent && !collapsed) ? 100001 : (focusedState ? 999 : 1)
    }}>
      {Platform.OS === 'web' ? (!showCollapsableInsteadOfInput && <View style={{
        flex: 1,
        maxWidth: 60,
        alignItems: 'center',
        justifyContent: 'center',
        alignContent: 'center',
        flexDirection: 'column',
        gap: 4,
        // Collapsed dense row: keep the leading icon inside (and centred within) the row height.
        ...(!isExpanded && {
          height: containerHeight,
          maxHeight: containerHeight,
          overflow: 'hidden' as const,
        }),
      }}>
        {/* AI Usage Stats - above robot animation when expanded */}
        {isExpanded && aiSearch && (
          <View style={{
            width: 50,
            height: 50,
            borderRadius: 25,
            backgroundColor: theme ? '#f3f4f6' : '#374151',
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: theme ? '#e5e7eb' : '#4b5563',
            marginBottom: 4,
          }}>
            {hasActiveSubscription ? (
              <MaterialIcons
                name="all-inclusive"
                size={28}
                color={theme ? '#22c55e' : '#10b981'}
              />
            ) : aiUsageLoading ? (
              <LoadingIndicator
                type="indeterminate"
                color={theme ? '#3b82f6' : '#60a5fa'}
              />
            ) : (
              <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{
                  fontSize: responsiveFontSize(screenWidth < 400 ? 14 : 16),
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  lineHeight: 18,
                }}>
                  {aiUsageStats?.currentCount || aiUsageStats?.thisMonth || 0}
                </Text>
                <Text style={{
                  fontSize: responsiveFontSize(screenWidth < 400 ? 9 : 10),
                  fontWeight: '500',
                  color: theme ? '#64748b' : '#94a3b8',
                  lineHeight: 12,
                }}>
                  /{aiUsageStats?.limit || 5}
                </Text>
              </View>
            )}
          </View>
        )}
        {focusedState ? <AnimatedIcon theme={theme} name="ellipsis-h" /> : (aiSearch ? <AnimatedLottieView source={require("@assets/animations/automation_robot.json")} autoPlay loop style={{ height: isExpanded ? 50 : Math.min(50, containerHeight), width: isExpanded ? 50 : Math.min(50, containerHeight) }} /> : (!hideInput ? <AnimatedIcon name="search" theme={theme} /> : null))}
      </View>)
        : (!showCollapsableInsteadOfInput && <View style={{ minWidth: 10 }} />)
      }
      {
        filterVisible ?
          <View style={{ position: 'absolute', minHeight: 300, top: 50, left: 100, backgroundColor: 'rgb(90,192,234)', borderRadius: 20, overflow: 'visible', zIndex: 20 }}>
            <FlatList data={searchParams} style={{ flex: 1 }} renderItem={({ item, index }) => { return (<RowItem item={item} index={index} />) }} />
          </View>
          : null
      }
      {
        filterArray.length > 0 &&
        <View style={{ flexDirection: 'column' }}>
          {
            filterArray.map((item, index) => {
              return (
                <View key={index} style={{ backgroundColor: 'rgb(90,192,234)' }}>
                  <Text>
                    {item}
                  </Text>
                </View>
              )
            })

          }
        </View>
      }
      {collapsableComponent ? (
        <>
          {/* BACKDROP (only when expanded) */}
          {!collapsed && (
            <Pressable
              onPress={() => setCollapsed(true)}
              style={{
                position: 'absolute',
                top: -Dimensions.get('window').height,
                left: -Dimensions.get('window').width,
                right: -Dimensions.get('window').width,
                bottom: -Dimensions.get('window').height,
                zIndex: 9999,
                backgroundColor: 'rgba(0,0,0,0.4)',
              }}
            />
          )}

          {/* PANEL */}
          <Animatable.View
            transition={["minHeight", "top", 'marginHorizontal']}
            style={{
              backgroundColor: theme ? '#ffffff' : '#1f2937',
              borderRadius: 20,
              borderWidth: 1,
              zIndex: 100000,
              borderColor: theme ? '#e5e7eb' : '#4b5563',
              padding: 6,
              marginHorizontal: collapsed ? 0 : -35,
              flex: 1,
              top: 20,
              width: '100%',
              paddingHorizontal: 5,
              minHeight: collapsed ? 60 : 250,
            }}
          >
            {collapsed ? (
              <Pressable
                onPress={() => setCollapsed(false)}
                style={{
                  padding: 10,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: theme ? '#1f2937' : '#f9fafb',
                    flex: 1,
                  }}
                >
                  {collapsedContent ?? 'Expand'}
                </Text>

                <FontAwesome6
                  name="gear"
                  size={16}
                  color={theme ? '#1f2937' : '#f9fafb'}
                />
              </Pressable>
            ) : (
              <ScrollView contentContainerStyle={{ padding: 10 }}>
                {collapsableComponent}

                <Pressable
                  onPress={() => setCollapsed(true)}
                  style={{
                    marginTop: 10,
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 10,
                    padding: 10,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      color: theme ? '#1f2937' : '#f9fafb',
                    }}
                  >
                    Collapse
                  </Text>

                  <FontAwesome6
                    name="caret-up"
                    size={16}
                    color={theme ? '#1f2937' : '#f9fafb'}
                  />
                </Pressable>
              </ScrollView>
            )}
          </Animatable.View>
        </>
      ) : (
        !hideInput && (
            <TextInput
              ref={searchRef}
              returnKeyLabel='Search'
              returnKeyType={multiline ? 'default' : 'search'}
              multiline={multiline}
              onFocus={() => {
                setFocusedState(true);
                onFocus?.();
              }}
              onBlur={() => {
                setFocusedState(false);
                onBlur?.();
              }}
              style={[searchStyles.inputStyle, {
                flex: 1,
                flexShrink: 1,
                borderRadius: inputBorderRadius,
                maxWidth: (focusedState && aiCallback) ? 1000 : 600,
                color: theme ? '#1f2937' : '#f9fafb',
                fontSize: resolvedFontSize,
                lineHeight: multiline ? undefined : singleLineLineHeight,
                outlineStyle: 'none' as any, // Remove web outline
                alignSelf: (focusedState && aiCallback) ? 'stretch' : 'center',
                paddingRight: isExpanded ? 60 : (aiSearch ? (Platform.OS === 'web' ? 90 : 85) : 50),
                paddingHorizontal: 15,
                minHeight: isExpanded
                  ? 120
                  : (inputMinHeight ?? (Platform.OS === 'android' ? 44 : undefined)),
                maxHeight: isExpanded
                  ? (Platform.OS === 'web' ? 240 : 300)
                  : Platform.OS === 'web'
                    ? inputMinHeight
                    : undefined,
                textAlignVertical: isExpanded ? 'top' as const : 'center' as const,
                paddingTop: isExpanded ? 12 : (Platform.OS === 'android' ? 12 : undefined),
                paddingBottom: isExpanded ? 12 : (Platform.OS === 'android' ? 12 : undefined),
                paddingVertical: isExpanded ? undefined : (Platform.OS === 'android' ? 12 : undefined),
              }]}
              value={displayValue}
              onChangeText={(newText) => {
                if (newText.includes('/')) {
                  setFilterVisible(true);
                }
                setText(newText);
                onValueChange?.(newText);
                handler(newText);
              }}
              placeholder={placeHolder || 'Search'}
              placeholderTextColor={theme ? "#9ca3af" : "#6b7280"}
              accessibilityLabel="Search input"
              accessibilityRole="search"
              blurOnSubmit={!multiline}
              {...(isExpanded && aiSearch && onSubmitAI
                ? Platform.OS === 'web'
                  ? { onKeyDown: handleWebAISubmitKeyDown as any }
                  : { onKeyPress: handleNativeAISubmitKeyPress }
                : {})}
              onSubmitEditing={
                isExpanded && aiSearch && onSubmitAI && !multiline
                  ? () => {
                    void onSubmitAI();
                  }
                  : undefined
              }
            />
        )
      )}
      {/* Button container - horizontal when collapsed, vertical column when expanded */}
      {(!isExpanded && !hideInput) ? (
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          position: 'absolute' as const,
          right: 0,
          height: '100%',
          zIndex: 20,
          pointerEvents: 'box-none' as const,
          gap: aiSearch ? -12 : 0,
        }}>
          <Pressable
            style={{
              opacity: text !== '' ? 1 : 0.2,
              justifyContent: 'center',
              alignItems: 'center',
              alignContent: 'center',
              width: 50,
              height: 50,
              zIndex: 21,
              marginRight: aiSearch ? -8 : 0,
            }}
            disabled={!hasValue}
            hitSlop={{
              top: 10,
              bottom: 10,
              left: 10,
              right: 10,
            }}
            onPress={clearSearch}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            accessibilityState={{ disabled: !hasValue }}
          >
            <MaterialIcons
              name="cancel"
              size={24}
              style={{ overflow: 'visible', width: 50 }}
              color={hasValue ? (theme ? 'black' : 'white') : 'rgba(128,128,128,0.3)'}
            />
          </Pressable>
          {
            aiSearch &&
            <Pressable
              disabled={!hasValue || AILoading}
                onPress={async () => {
                if (!displayValue || !aiCallback) {
                  console.warn('⚠️ Wand button: Missing text or callback', { text: !!displayValue, callback: !!aiCallback });
                  return;
                }
                  try {
                  setAILoading(true);
                  await aiCallback(displayValue);
                } catch (err) {
                  console.error('❌ AI Search error:', err);
                } finally {
                  setAILoading(false);
                }
              }}
              style={{
                opacity: hasValue && !AILoading ? 1 : 0.2,
                width: 50,
                height: 50,
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                zIndex: 22,
                backgroundColor: 'transparent',
                marginLeft: -8,
              }}
              hitSlop={{
                top: 15,
                bottom: 15,
                left: 15,
                right: 15,
              }}
              accessibilityRole="button"
              accessibilityLabel="AI search"
              accessibilityState={{ disabled: !hasValue || AILoading }}
            >
              {
                AILoading ?
                  <LoadingIndicator type={undefined} color={"rgb(90,192,234)"} />
                  :
                  <FontAwesome name="magic" size={24} color={theme ? "black" : "white"} />
              }
            </Pressable>
          }
        </View>
      ) : null}
    </View>
  );
});

export default InteractiveSearch;
